import {ReactNode} from 'react'
import {toast, ToastOptions, ToastPosition, TypeOptions} from 'react-toastify'
import {nanoid} from 'nanoid'

import {ToastCheckIcon, ToastCloseIcon, TriangleWarningIcon} from '../SvgIcon'
import {RenderIf} from '../RenderIf'
import {colorCodes} from 'definitions/constant'

export const toastStyles = {
  success: {
    style: {
      background: '#081d15',
      border: `1px solid ${colorCodes.green100}`,
      borderRadius: 0,
      color: colorCodes.green100,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <ToastCheckIcon />
      </div>
    ),
    closeButton: false,
  },
  error: {
    style: {
      background: '#3a1d1a',
      border: `1px solid ${colorCodes.red100}`,
      borderRadius: 0,
      color: colorCodes.red100,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <ToastCloseIcon />
      </div>
    ),
    closeButton: false,
  },
  info: {
    style: {
      background: `linear-gradient(0deg, rgba(0, 190, 255, 0.3), rgba(0, 190, 255, 0.3)), ${colorCodes.dopeBlack}`,
      border: `1px solid ${colorCodes.cyanBright}`,
      borderRadius: 0,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <ToastCloseIcon strokeColor="text-dope-cyan-bright" />
      </div>
    ),
    closeButton: false,
  },
  warning: {
    style: {
      background: `linear-gradient(0deg, rgba(247, 200, 57, 0.2), rgba(247, 200, 57, 0.2)), ${colorCodes.dopeBlack}`,
      border: `1px solid ${colorCodes.yellow100}`,
      borderRadius: 0,
      color: colorCodes.yellow100,
    },
    icon: (
      <div style={{marginLeft: '-2px'}}>
        <TriangleWarningIcon strokeColor="text-yellow-100" />
      </div>
    ),
    closeButton: false,
  },
}

interface Props {
  message: string | ReactNode | (() => ReactNode) | unknown
  type: TypeOptions
  position?: ToastPosition
  customId?: string
  icon?: ReactNode
  style?: React.CSSProperties
  autoClose?: number | false
  hideIcon?: boolean
  multipleToast?: boolean
}

const Toast = ({
  message,
  type,
  position = 'bottom-center',
  customId,
  icon,
  style = {},
  autoClose = 5000,
  hideIcon = false,
  multipleToast = false,
}: Props) => {
  const toastId = customId ?? nanoid()

  if (message === null || message === undefined || message === '') {
    return null
  }

  const resolveMessage = (): ReactNode => {
    // If it's already a valid React node, just return it
    // If it's a function (thunk), call it to get the node
    if (typeof message === 'function') {
      // Narrow to zero-arg thunk returning ReactNode
      return (message as () => ReactNode)()
    }
    return message as ReactNode
  }

  const getToastProps = (newProps: ToastOptions = {}) => {
    const baseProps = {
      toastId,
      position,
      autoClose,
      closeOnClick: true,
      ...newProps,
      icon: hideIcon ? null : icon ?? newProps?.icon,
      style: {
        ...newProps.style,
        ...style,
      },
      role: 'alert',
    }

    // Map customId to toastId for proper deduplication
    if (customId) {
      baseProps.toastId = customId
    }

    return baseProps
  }

  let props: ToastOptions = {}

  // Allow multiple toasts to stack based on prop multipleToast
  if (!multipleToast) {
    toast.dismiss()
  }

  switch (type) {
    case 'success':
      props = getToastProps(toastStyles.success)
      break
    case 'error':
      props = getToastProps(toastStyles.error)
      break
    case 'warning':
      props = getToastProps(toastStyles.warning)
      break
    case 'info':
      props = getToastProps(toastStyles.info)
      break
    default:
      props = getToastProps()
      break
  }

  const isPersistent = autoClose === false
  const content = resolveMessage()

  const renderContent = () => (
    <>
      <RenderIf isTrue={isPersistent}>
        <div className="flex items-center gap-2 w-full">
          <div className="flex-1 whitespace-pre-wrap">{content}</div>
        </div>
      </RenderIf>
      <RenderIf isTrue={!isPersistent}>
        <div className="whitespace-pre-wrap">{content}</div>
      </RenderIf>
    </>
  )

  return toast(renderContent(), props)
}

export default Toast
